import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL, { getImageUrl } from '../config';
import './LostAndFound.css';

interface LostFoundItem {
    _id: string;
    itemName: string;
    description: string;
    type: 'lost' | 'found';
    location: string;
    contactName: string;
    phoneNumber: string;
    isPhonePublic: boolean;
    imageUrl?: string;
    status: 'active' | 'resolved';
    reportedBy?: string;
    createdAt: string;
}

const LostAndFound: React.FC = () => {
    const { accessToken, user, loading: authLoading } = useAuth();
    const history = useHistory();

    const [items, setItems] = useState<LostFoundItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<'all' | 'lost' | 'found'>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        itemName: '',
        description: '',
        type: 'lost' as 'lost' | 'found',
        location: '',
        contactName: user?.name || '',
        phoneNumber: '',
        isPhonePublic: false,
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);

    const fetchItems = useCallback(async () => {
        try {
            const typeQuery = filterType !== 'all' ? `?type=${filterType}` : '';
            const res = await fetch(`${API_BASE_URL}/api/lost-found${typeQuery}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (data.items) setItems(data.items);
        } catch (err) {
            console.error('Failed to fetch lost/found items:', err);
        } finally {
            setLoading(false);
        }
    }, [accessToken, filterType]);

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) {
                fetchItems();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, fetchItems, history]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;

        const { itemName, description, type, location, contactName, phoneNumber } = formData;
        if (!itemName || !description || !location || !contactName || !phoneNumber) {
            alert('Please fill in all required fields.');
            return;
        }

        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('itemName', itemName);
            fd.append('description', description);
            fd.append('type', type);
            fd.append('location', location);
            fd.append('contactName', contactName);
            fd.append('phoneNumber', phoneNumber);
            fd.append('isPhonePublic', String(formData.isPhonePublic));
            if (imageFile) fd.append('itemImage', imageFile);

            const res = await fetch(`${API_BASE_URL}/api/lost-found`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
                body: fd,
            });

            if (res.ok) {
                setShowForm(false);
                setFormData({ itemName: '', description: '', type: 'lost', location: '', contactName: user?.name || '', phoneNumber: '', isPhonePublic: false });
                setImageFile(null);
                setImagePreview(null);
                fetchItems();
            } else {
                const data = await res.json();
                alert(data.message || 'Failed to submit report.');
            }
        } catch (err) {
            console.error('Submit error:', err);
            alert('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResolve = async (id: string) => {
        if (!window.confirm('Mark this item as resolved?')) return;
        try {
            await fetch(`${API_BASE_URL}/api/lost-found/${id}/resolve`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            fetchItems();
        } catch (err) {
            console.error('Resolve error:', err);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this report permanently?')) return;
        try {
            await fetch(`${API_BASE_URL}/api/lost-found/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            fetchItems();
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const formatPhoneForWhatsApp = (phone: string) => {
        return phone.replace(/[^0-9]/g, '');
    };

    const getWhatsAppLink = (item: LostFoundItem) => {
        const cleanPhone = formatPhoneForWhatsApp(item.phoneNumber);
        const message = encodeURIComponent(
            `Hi ${item.contactName}, I'm at the wedding and saw your post about the "${item.itemName}". Is it still ${item.type === 'lost' ? 'missing' : 'available'}?`
        );
        return `https://wa.me/${cleanPhone}?text=${message}`;
    };

    const filteredItems = items.filter(item => {
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                item.itemName.toLowerCase().includes(q) ||
                item.description.toLowerCase().includes(q) ||
                item.location.toLowerCase().includes(q)
            );
        }
        return true;
    });

    if (loading) {
        return (
            <div className="lf-page">
                <div className="lf-loading">
                    <div className="lf-spinner"></div>
                    <p>Loading Lost & Found board...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="lf-page">
            {/* Header */}
            <header className="lf-header">
                <div className="lf-header-content">
                    <div className="lf-header-icon">🔍</div>
                    <div>
                        <h1>Lost & Found</h1>
                        <p>Community board for the wedding</p>
                    </div>
                </div>
                <button className="lf-report-btn" onClick={() => setShowForm(true)}>
                    <span>+</span> Report Item
                </button>
            </header>

            {/* Filter Bar */}
            <div className="lf-filter-bar">
                <div className="lf-filter-tabs">
                    {(['all', 'lost', 'found'] as const).map(f => (
                        <button
                            key={f}
                            className={`lf-tab ${filterType === f ? 'active' : ''} ${f === 'lost' ? 'tab-lost' : f === 'found' ? 'tab-found' : ''}`}
                            onClick={() => setFilterType(f)}
                        >
                            {f === 'all' ? '📋 All' : f === 'lost' ? '🔴 Lost' : '🟢 Found'}
                        </button>
                    ))}
                </div>
                <div className="lf-search">
                    <input
                        type="text"
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="lf-search-clear" onClick={() => setSearchQuery('')}>✕</button>
                    )}
                </div>
            </div>

            {/* Items Grid */}
            <main className="lf-grid">
                {filteredItems.length === 0 ? (
                    <div className="lf-empty">
                        <span className="lf-empty-icon">🎉</span>
                        <h3>No reports yet</h3>
                        <p>Great news! No items have been reported {filterType !== 'all' ? `as ${filterType}` : ''}.</p>
                    </div>
                ) : (
                    filteredItems.map(item => (
                        <div key={item._id} className={`lf-card ${item.type}`}>
                            <div className={`lf-badge ${item.type}`}>
                                {item.type === 'lost' ? '🔴 LOST' : '🟢 FOUND'}
                            </div>

                            {item.imageUrl && (
                                <div className="lf-card-image" onClick={() => setLightboxImage(getImageUrl(item.imageUrl!))}>
                                    <img src={getImageUrl(item.imageUrl)} alt={item.itemName} />
                                </div>
                            )}

                            <div className="lf-card-body">
                                <h3 className="lf-item-name">{item.itemName}</h3>
                                <p className="lf-item-desc">{item.description}</p>

                                <div className="lf-meta">
                                    <div className="lf-meta-row">
                                        <span className="lf-meta-icon">📍</span>
                                        <span>{item.location}</span>
                                    </div>
                                    <div className="lf-meta-row">
                                        <span className="lf-meta-icon">👤</span>
                                        <span>{item.contactName}</span>
                                    </div>
                                    <div className="lf-meta-row">
                                        <span className="lf-meta-icon">🕐</span>
                                        <span>{new Date(item.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                                    </div>
                                </div>

                                {/* Contact Actions */}
                                {item.phoneNumber !== '***HIDDEN***' && (
                                    <div className="lf-contact-actions">
                                        <a href={`tel:${item.phoneNumber}`} className="lf-action-btn call-btn">
                                            📞 Call {item.contactName.split(' ')[0]}
                                        </a>
                                        <a
                                            href={getWhatsAppLink(item)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="lf-action-btn wa-btn"
                                        >
                                            💬 WhatsApp
                                        </a>
                                    </div>
                                )}
                                {item.phoneNumber === '***HIDDEN***' && (
                                    <div className="lf-private-notice">
                                        🔒 Contact info is private — ask Admin for details
                                    </div>
                                )}

                                {/* Actions for owner / admin */}
                                {(user?.role === 'admin' || item.reportedBy === user?.id) && item.status === 'active' && (
                                    <div className="lf-owner-actions">
                                        <button className="lf-resolve-btn" onClick={() => handleResolve(item._id)}>
                                            ✅ Mark Resolved
                                        </button>
                                        {user?.role === 'admin' && (
                                            <button className="lf-delete-btn" onClick={() => handleDelete(item._id)}>
                                                🗑️ Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </main>

            {/* Submit Form Modal */}
            {showForm && (
                <div className="lf-modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="lf-modal" onClick={e => e.stopPropagation()}>
                        <button className="lf-modal-close" onClick={() => setShowForm(false)}>&times;</button>
                        <h2>Report an Item</h2>

                        <form onSubmit={handleSubmit}>
                            {/* Type Toggle */}
                            <div className="lf-type-toggle">
                                <button
                                    type="button"
                                    className={`toggle-option ${formData.type === 'lost' ? 'active lost' : ''}`}
                                    onClick={() => setFormData(p => ({ ...p, type: 'lost' }))}
                                >
                                    🔴 I Lost Something
                                </button>
                                <button
                                    type="button"
                                    className={`toggle-option ${formData.type === 'found' ? 'active found' : ''}`}
                                    onClick={() => setFormData(p => ({ ...p, type: 'found' }))}
                                >
                                    🟢 I Found Something
                                </button>
                            </div>

                            <div className="lf-form-group">
                                <label>Item Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Gold Bracelet, Silk Dupatta"
                                    value={formData.itemName}
                                    onChange={e => setFormData(p => ({ ...p, itemName: e.target.value }))}
                                    required
                                />
                            </div>

                            <div className="lf-form-group">
                                <label>Description *</label>
                                <textarea
                                    placeholder="Describe the item in detail (color, size, brand, etc.)"
                                    value={formData.description}
                                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                                    required
                                    rows={3}
                                />
                            </div>

                            <div className="lf-form-group">
                                <label>Location *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Banquet Hall, Parking Lot, Room 204"
                                    value={formData.location}
                                    onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
                                    required
                                />
                            </div>

                            <div className="lf-form-row">
                                <div className="lf-form-group">
                                    <label>Your Name *</label>
                                    <input
                                        type="text"
                                        placeholder="Your Name"
                                        value={formData.contactName}
                                        onChange={e => setFormData(p => ({ ...p, contactName: e.target.value }))}
                                        required
                                    />
                                </div>
                                <div className="lf-form-group">
                                    <label>Phone Number *</label>
                                    <input
                                        type="tel"
                                        placeholder="+91 98765 43210"
                                        value={formData.phoneNumber}
                                        onChange={e => setFormData(p => ({ ...p, phoneNumber: e.target.value }))}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="lf-privacy-toggle">
                                <label className="lf-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.isPhonePublic}
                                        onChange={e => setFormData(p => ({ ...p, isPhonePublic: e.target.checked }))}
                                    />
                                    <span className="lf-checkmark"></span>
                                    <span>Make my number visible to other guests</span>
                                </label>
                                <p className="lf-privacy-hint">If unchecked, only the Admin can see your number.</p>
                            </div>

                            <div className="lf-form-group">
                                <label>Photo (Optional)</label>
                                <div className="lf-image-upload" onClick={() => document.getElementById('lf-image-input')?.click()}>
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Preview" className="lf-image-preview" />
                                    ) : (
                                        <div className="lf-upload-placeholder">
                                            <span>📷</span>
                                            <p>Tap to add a photo</p>
                                        </div>
                                    )}
                                    <input
                                        id="lf-image-input"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            </div>

                            <button type="submit" className="lf-submit-btn" disabled={submitting}>
                                {submitting ? 'Submitting...' : '📢 Submit Report'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Image Lightbox */}
            {lightboxImage && (
                <div className="lf-lightbox" onClick={() => setLightboxImage(null)}>
                    <img src={lightboxImage} alt="Full view" />
                </div>
            )}
        </div>
    );
};

export default LostAndFound;
